import React, { createContext, useContext, useMemo, useState, useEffect, useLayoutEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme';
import { AppGlobalStyles } from './animations';
import { DEFAULT_PALETTE, paletteOf, applyPaletteVars } from './palettes';

const ThemeModeContext = createContext({ mode: 'light', toggleMode: () => {}, palette: DEFAULT_PALETTE });
export const useThemeMode = () => useContext(ThemeModeContext);

const KEY = 'ssatweb.themeMode';
const PREVIEW = process.env.REACT_APP_PREVIEW === '1';   // review-only builds (src/preview)

export function ThemeModeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    if (PREVIEW && new URLSearchParams(window.location.search).get('theme') === 'dark') return 'dark';   // review-only
    try {
      const saved = window.localStorage.getItem(KEY);
      if (saved === 'dark' || saved === 'light') return saved;
    } catch (e) { /* storage unavailable */ }
    return 'light';
  });
  const palette = DEFAULT_PALETTE;
  useEffect(() => { try { window.localStorage.setItem(KEY, mode); } catch (e) { /* ignore */ } }, [mode]);
  useLayoutEffect(() => { applyPaletteVars(paletteOf(palette), mode); }, [palette, mode]);   // CSS variables for gradients / sidebar / glows
  const theme = useMemo(() => createAppTheme(mode, palette), [mode, palette]);
  const value = useMemo(() => ({ mode, toggleMode: () => setMode(m => (m === 'dark' ? 'light' : 'dark')), palette }), [mode, palette]);
  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppGlobalStyles />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
